import copyIcon from '@iconify/icons-material-symbols/content-copy-rounded';
import { Icon } from '@iconify/react';
import { Box, IconButton, Link, Tooltip } from '@mui/material';
import React, { useState } from 'react';

import useLocales from 'hooks/useLocales';

import { parseRichText, RichInline } from './richTextParser';

const MONOSPACE = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

const renderInline = (pieces: RichInline[], keyPrefix: string): React.ReactNode =>
    pieces.map((piece, index) => {
        const key = `${keyPrefix}-${index}`;
        switch (piece.kind) {
            case 'link':
                return (
                    <Link key={key} href={piece.href} target='_blank' rel='noopener noreferrer'>
                        {piece.text}
                    </Link>
                );
            case 'bold':
                return <strong key={key}>{piece.text}</strong>;
            case 'code':
                return (
                    <Box
                        key={key}
                        component='code'
                        sx={{
                            fontFamily: MONOSPACE,
                            fontSize: '0.9em',
                            bgcolor: 'grey.300',
                            px: 0.5,
                            borderRadius: 0.5,
                        }}
                    >
                        {piece.text}
                    </Box>
                );
            default:
                return <React.Fragment key={key}>{piece.text}</React.Fragment>;
        }
    });

const CodeBlock: React.FunctionComponent<{ language: string; code: string }> = ({ language, code }) => {
    const { translate } = useLocales();
    const [copied, setCopied] = useState(false);

    const copy = (): void => {
        navigator.clipboard
            ?.writeText(code)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
            })
            .catch(() => undefined);
    };

    return (
        <Box sx={{ position: 'relative', my: 1, borderRadius: 1, bgcolor: 'grey.900', color: 'grey.100' }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1.5,
                    pt: 0.5,
                    fontSize: 12,
                    color: 'grey.500',
                }}
            >
                <span>{language}</span>
                <Tooltip title={translate(copied ? 'chat_code_copied' : 'chat_code_copy')}>
                    <IconButton
                        size='small'
                        onClick={copy}
                        sx={{ color: 'grey.400' }}
                        aria-label={translate('chat_code_copy')}
                    >
                        <Icon icon={copyIcon} width={16} height={16} />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box
                component='pre'
                sx={{ m: 0, px: 1.5, pb: 1.5, overflowX: 'auto', fontFamily: MONOSPACE, fontSize: 13, lineHeight: 1.5 }}
            >
                <code>{code}</code>
            </Box>
        </Box>
    );
};

// Answers as React nodes, never HTML, so model output stays inert. See
// richTextParser.ts for which markdown is understood.
const ChatRichText: React.FunctionComponent<{ text: string }> = ({ text }) => (
    <>
        {parseRichText(text).map((block, blockIndex) =>
            block.kind === 'code' ? (
                // eslint-disable-next-line react/no-array-index-key
                <CodeBlock key={blockIndex} language={block.language} code={block.code} />
            ) : (
                // eslint-disable-next-line react/no-array-index-key
                <React.Fragment key={blockIndex}>
                    {block.lines.map((line, lineIndex) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <React.Fragment key={lineIndex}>
                            {renderInline(line, `${blockIndex}-${lineIndex}`)}
                            {lineIndex < block.lines.length - 1 && <br />}
                        </React.Fragment>
                    ))}
                </React.Fragment>
            ),
        )}
    </>
);

export default ChatRichText;
